import { Controller } from "@hotwired/stimulus"
import { parseDecklist, formatDecklist, previewSummary } from "../lib/precon_decklist"

const DEBOUNCE_MS = 300

export default class extends Controller {
  static targets = [
    "dialog",
    "textarea",
    "status",
    "confirm",
    "copy",
    "title",
    "hint",
    "exportPicker",
    "preconSelect",
    "importPane"
  ]
  static values = {
    catalog: { type: Array, default: [] },
    importUrl: { type: String, default: "" },
    initialText: { type: String, default: "" },
    exportPrecons: { type: Array, default: [] }
  }

  connect() {
    this._timer = null
    this._intent = "import"
    this._preconsById = Object.fromEntries(
      (this.exportPreconsValue || []).map((row) => [String(row.id), row])
    )
    if (this.initialTextValue && this.hasTextareaTarget) {
      this.textareaTarget.value = this.initialTextValue
      this.refreshPreview()
      queueMicrotask(() => this.openImport())
    }
  }

  disconnect() {
    if (this._timer) clearTimeout(this._timer)
  }

  openImport(event) {
    event?.preventDefault()
    this._intent = "import"
    this._exportIdentity = null
    this.setChrome("Import decklist", "Paste lines like: 1 Card Name. Hero, background, and subclass are plain 1 Name lines too.")
    this.showExportPicker(false)
    if (this.hasTextareaTarget) {
      this.textareaTarget.readOnly = false
      if (!this.textareaTarget.value.trim()) this.textareaTarget.value = this.initialTextValue || ""
    }
    this.showConfirm(true)
    this.showCopy(false)
    this.openDialog()
    this.refreshPreview()
    this.textareaTarget?.focus()
  }

  openExport(event) {
    event?.preventDefault()
    this._intent = "export"
    this._exportIdentity = null
    this.setChrome("Export decklist", "Choose a precon, then copy the list.")
    this.showExportPicker(true)
    if (this.hasPreconSelectTarget) this.preconSelectTarget.value = ""
    if (this.hasTextareaTarget) {
      this.textareaTarget.readOnly = true
      this.textareaTarget.value = ""
    }
    this.showConfirm(false)
    this.showCopy(true)
    if (this.hasCopyTarget) this.copyTarget.disabled = true
    this.setStatus("Select a precon to export.", false)
    this.openDialog()
  }

  onExportSelect() {
    const id = this.hasPreconSelectTarget ? this.preconSelectTarget.value : ""
    const row = this._preconsById[String(id)]
    if (!row) {
      if (this.hasTextareaTarget) this.textareaTarget.value = ""
      if (this.hasCopyTarget) this.copyTarget.disabled = true
      this._exportIdentity = null
      this.setStatus("Select a precon to export.", false)
      return
    }

    this._exportIdentity = {
      hero_card_id: row.hero_card_id,
      background_card_id: row.background_card_id,
      subclass_card_id: row.subclass_card_id
    }
    const text = formatDecklist({
      heroCardId: row.hero_card_id,
      backgroundCardId: row.background_card_id,
      subclassCardId: row.subclass_card_id,
      counts: row.counts || {},
      catalog: this.catalogValue || []
    })
    if (this.hasTextareaTarget) this.textareaTarget.value = text
    if (this.hasCopyTarget) this.copyTarget.disabled = !text.trim()
    this.refreshPreview()
  }

  onDialogClose() {
    // no-op hook for dialog native close
  }

  close(event) {
    event?.preventDefault()
    this.dialogTarget?.close()
  }

  onInput() {
    if (this._intent !== "import") return
    if (this._timer) clearTimeout(this._timer)
    this._timer = setTimeout(() => this.refreshPreview(), DEBOUNCE_MS)
  }

  confirm(event) {
    event?.preventDefault()
    if (this._intent !== "import") return

    const parsed = this.parseCurrent()
    if (parsed.errors.length) {
      this.renderStatus(parsed, true)
      return
    }
    if (!parsed.matched_lines) {
      this.setStatus("Import needs at least one main-deck card line (e.g. 1 Card Name)", true)
      return
    }
    this.submitListImport()
  }

  async copy(event) {
    event?.preventDefault()
    const text = this.hasTextareaTarget ? this.textareaTarget.value : ""
    if (!text.trim()) {
      this.setStatus("Select a precon to export first.", true)
      return
    }
    try {
      await navigator.clipboard.writeText(text)
      this.setStatus("Copied to clipboard", false)
    } catch {
      this.setStatus("Could not copy — select and copy manually", true)
    }
  }

  openDialog() {
    if (!this.hasDialogTarget) return
    if (typeof this.dialogTarget.showModal === "function") {
      this.dialogTarget.showModal()
    } else {
      this.dialogTarget.setAttribute("open", "")
    }
  }

  setChrome(title, hint) {
    if (this.hasTitleTarget) this.titleTarget.textContent = title
    if (this.hasHintTarget) this.hintTarget.textContent = hint
  }

  showExportPicker(show) {
    if (this.hasExportPickerTarget) this.exportPickerTarget.hidden = !show
    if (this.hasImportPaneTarget) {
      // Keep textarea visible for both modes; only the picker is export-specific.
      this.importPaneTarget.hidden = false
    }
  }

  showConfirm(show) {
    if (!this.hasConfirmTarget) return
    this.confirmTarget.hidden = !show
  }

  showCopy(show) {
    if (!this.hasCopyTarget) return
    this.copyTarget.hidden = !show
  }

  parseCurrent() {
    const text = this.hasTextareaTarget ? this.textareaTarget.value : ""
    return parseDecklist(text, this.catalogValue || [])
  }

  refreshPreview() {
    const parsed = this.parseCurrent()
    this.renderStatus(parsed, parsed.errors.length > 0)
  }

  renderStatus(parsed, isError) {
    if (!this.hasStatusTarget) return
    const identity =
      this._intent === "export" && this._exportIdentity
        ? { ...this._exportIdentity, catalog: this.catalogValue || [] }
        : { catalog: this.catalogValue || [] }
    const lines = [previewSummary(parsed, identity)]
    if (parsed.errors?.length) {
      lines.push(...parsed.errors.slice(0, 8).map((e) => `• ${e}`))
      if (parsed.errors.length > 8) lines.push(`• …and ${parsed.errors.length - 8} more`)
    }
    if (parsed.warnings?.length) {
      lines.push(...parsed.warnings.slice(0, 4).map((w) => `• ${w}`))
    }
    this.statusTarget.textContent = lines.join("\n")
    this.statusTarget.classList.toggle("precon-decklist__status--error", !!isError)
    if (this.hasConfirmTarget && this._intent === "import") {
      this.confirmTarget.disabled = parsed.errors.length > 0 || !parsed.matched_lines
    }
  }

  setStatus(message, isError) {
    if (!this.hasStatusTarget) return
    // Keep identity headers visible even for short messages.
    const identity =
      this._intent === "export" && this._exportIdentity
        ? { ...this._exportIdentity, catalog: this.catalogValue || [] }
        : { catalog: this.catalogValue || [] }
    const empty = {
      matched_lines: 0,
      main_deck_size: 0,
      errors: [],
      warnings: []
    }
    const header = previewSummary(empty, identity).split("\n").slice(0, 3).join("\n")
    this.statusTarget.textContent = `${header}\n${message}`
    this.statusTarget.classList.toggle("precon-decklist__status--error", !!isError)
  }

  submitListImport() {
    const form = this.element.querySelector("form[data-precon-decklist-form]")
    if (!form) return
    if (typeof form.requestSubmit === "function") {
      form.requestSubmit()
    } else {
      form.submit()
    }
  }
}
